import SysAdminUserFormComponent from '../components/sysadmin-user-form.component';
import { SysAdminUserUpdate } from '../types/sysadmin-user.types';
import { useSysAdminUserGet, useSysAdminUserUpdate } from '../hooks/use-sysadmin-user.hook';
import { message } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  extractCreateError,
  extractValidationError,
} from '../../../../../services/extract-api-error.service';
import { useEffect, useState, useCallback } from 'react';
import HeaderRow from '../../../../common/HeaderRow';
import { ValidationError } from '../../../../../types/api-response.types';
import { useTranslation } from 'react-i18next';
import ErrorFallback from '../../../../common/ErrorFallback';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { DefaultValues } from 'react-hook-form';
import { WithSysAdminPermissionCheck } from '../../../../auth/WithSysAdminPermissionCheck';
import PageTitle from '@components/common/PageTitle';

interface FormSubmitHandler {
  (data: SysAdminUserUpdate, resetForm: () => void): void;
}

const SysAdminUserUpdateContainer = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [formDefaultValues, setFormDefaultValues] = useState<DefaultValues<SysAdminUserUpdate>>({});
  const { data, isLoading, error } = useSysAdminUserGet(id ?? '');
  const { mutateAsync: updateUser, isPending } = useSysAdminUserUpdate();

  const extractFormDefaultValues = useCallback(async () => {
    if (!data?.data) return;
    const { data: userData } = data;

    setFormDefaultValues({
      role: userData.role.name ?? undefined,
    });
  }, [data]);

  useEffect(() => {
    let isMounted = true; // to prevent memory leaks
    extractFormDefaultValues();
    return () => {
      isMounted = false;
    };
  }, [extractFormDefaultValues]);

  const onFormSubmit: FormSubmitHandler = useCallback(
    async (data, resetForm) => {
      if (!id) return;
      // Reset validation errors on new submission
      setValidationErrors([]);
      try {
        await updateUser({ id, data });
        message.success(t('crud.update.success', { entity: t('users.title') }));
        navigate(-1);
      } catch (error: any) {
        const errorFields = extractValidationError(error);
        setValidationErrors(errorFields);
        const errorMessage = extractCreateError(
          error,
          t('crud.update.error', { entity: t('users.title') })
        );
        message.error(errorMessage);
      }
    },
    [id, updateUser, t, navigate]
  );

  if (!id) {
    return (
      <ErrorFallback
        details={t('crud.details.errorLoadingData')}
        icon={<ExclamationCircleOutlined />}
        error={new Error(t('common.invalidIdProvided'))}
        resetErrorBoundary={() => {}}
      />
    );
  }

  if (error) {
    return (
      <>
        <PageTitle title={t('crud.update.title', { entity: t('users.title') })} />
        <HeaderRow
          title={t('crud.update.title', { entity: t('users.title') })}
          showBackButton={true}
        />
        <ErrorFallback
          details={t('common.errorLoadingData')}
          icon={<ExclamationCircleOutlined />}
          error={error}
          resetErrorBoundary={() => {}}
        />
      </>
    );
  }

  return (
    <>
      <PageTitle title={t('crud.update.title', { entity: t('users.title') })} />
      <HeaderRow
        title={t('crud.update.title', { entity: t('users.title') })}
        showBackButton={true}
        isSysAdmin={true}
      />
      <div className="row mt-3">
        <SysAdminUserFormComponent
          onFormSubmit={onFormSubmit}
          isLoading={isPending || isLoading}
          defaultValues={formDefaultValues}
          columns={3}
          validationErrors={validationErrors}
        />
      </div>
    </>
  );
};

export default WithSysAdminPermissionCheck(SysAdminUserUpdateContainer, 'user.update');
